import React, { useState } from 'react';
import { createSpecies, updateSpecies } from '../../services/speciesService';
import { SPECIES_CATEGORIES } from '../../utils/speciesCategories';

const HARVEST_CATEGORIES = {
  fishing: 'Fishing', hunting: 'Hunting', birds: 'Birds',
  plants_medicine: 'Plants & Medicine', environmental: 'Environmental', other: 'Other',
};
const PROFILE_FIELDS = {
  shortDescription: 'About', habitat: 'Habitat',
  seasonalNotes: 'Seasonal notes', stewardshipNote: 'Stewardship note',
};

export default function SpeciesEditorModal({ species, onClose, onSaved, canArchive, archived = false }) {
  const isNew = !species?._id;
  const [form, setForm] = useState({
    commonName: species?.commonName || '',
    scientificName: species?.scientificName || '',
    communityName: species?.communityName || '',
    category: species?.category || 'Fish',
    harvestCategory: species?.harvestCategory || '',
    alternateNames: (species?.alternateNames || []).join(', '),
    subType: species?.subType || '',
    ...Object.fromEntries(Object.keys(PROFILE_FIELDS).map((field) => [field, species?.[field] || ''])),
  });
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const change = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));

  const changeStatus = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await updateSpecies(species._id, { isActive: archived });
      onSaved(res.data, false);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Could not change species status. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMsg('');
    setSaving(true);
    const payload = {
      ...Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()])),
      alternateNames: form.alternateNames.split(',').map((name) => name.trim()).filter(Boolean),
    };
    if (isNew) payload.slug = form.commonName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    try {
      const res = isNew ? await createSpecies(payload) : await updateSpecies(species._id, payload);
      onSaved(res.data, isNew);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => { if (!saving) onClose(); }}>
      <div className="modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={isNew ? 'Add Species' : `Edit: ${species.commonName}`}>
        <div className="modal-header">
          <h3>{isNew ? 'Add Species' : `Edit: ${species.commonName}`}</h3>
          <button className="modal-close" disabled={saving} onClick={onClose} aria-label="Close">X</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <fieldset disabled={saving} style={{ border: 0, padding: 0 }}>
              <label>Common name
                <input value={form.commonName} onChange={(event) => change('commonName', event.target.value)} required />
              </label>
              <label>Scientific name
                <input value={form.scientificName} onChange={(event) => change('scientificName', event.target.value)} />
              </label>
              <label>Community name
                <input value={form.communityName} onChange={(event) => change('communityName', event.target.value)} />
              </label>
              <label>Species category
                <select value={form.category} onChange={(event) => change('category', event.target.value)}>
                  {SPECIES_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </label>
              <label>Default harvest category (shared)
                <select value={form.harvestCategory} onChange={(event) => change('harvestCategory', event.target.value)}>
                  <option value="">None</option>
                  {Object.entries(HARVEST_CATEGORIES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </label>
              <p>This shared default applies across communities. Custom categories belong to your community: use Harvest Setup - Assign species to harvest categories. Adding a harvest category or sub-type does not add a species.</p>
              <label>Alternate names (comma-separated)
                <input value={form.alternateNames} onChange={(event) => change('alternateNames', event.target.value)} />
              </label>
              <label>Sub-type
                <input value={form.subType} onChange={(event) => change('subType', event.target.value)} />
              </label>
              {Object.entries(PROFILE_FIELDS).map(([field, label]) => (
                <label key={field}>{label}
                  <textarea rows={3} value={form[field]} onChange={(event) => change(field, event.target.value)} />
                </label>
              ))}
              <p>Profile information is shared across communities. Manage area-specific guidance separately in Stewardship.</p>
              {msg && <p className="error-msg" role="alert">{msg}</p>}
              <div className="form-actions">
                <button type="submit">{saving ? 'Saving...' : isNew ? 'Create Species' : 'Save Changes'}</button>
                <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              </div>
              {!isNew && canArchive && (
                <div style={{ marginTop: 20 }}>
                  <p>Stewardship manages removal from this shared catalogue. Archiving hides a species
                    from new selections in all communities, without deleting existing harvest records or guidance.</p>
                  {archived ? (
                    <button type="button" onClick={changeStatus}>Restore species</button>
                  ) : confirmArchive ? (
                    <div role="group" aria-label="Confirm archive">
                      <p>Archive {species.commonName} for all communities?</p>
                      <button type="button" onClick={changeStatus}>Confirm archive</button>
                      <button type="button" onClick={() => setConfirmArchive(false)}>Keep species</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setConfirmArchive(true)}>Archive species</button>
                  )}
                </div>
              )}
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
}
